import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import cx from 'clsx'
import { app } from '@microsoft/teams-js'
import * as Sentry from '@sentry/react'
import { IconStar } from '@tabler/icons-react'

import Popup from '@components/Popup'
import Button from '@components/Button'
import { Input } from '@components/Form'
import { toast } from '@helpers/toaster'
import { requestErrorsHandler } from '@helpers/utils'
import useIsTeamsMobileHost from '@msteams/hooks/useIsTeamsMobileHost'
import { MARKETPLACE_REVIEW_URL } from '@msteams/constants'
import { dismiss, submitFeedback, submitRating, trackReviewClick } from '@msteams/api/reviewPrompt'

export const MAX_RATING = 5
export const FEEDBACK_MAX_LENGTH = 2000

const RATINGS = Array.from({ length: MAX_RATING }, (_, index) => index + 1)

enum Step {
  rate = 'rate',
  review = 'review',
  feedback = 'feedback',
}

const reportError = (err: unknown) => Sentry.captureException(err)

interface ReviewPromptPopupProps {
  onClose: () => void
}

const ReviewPromptPopup: React.FC<ReviewPromptPopupProps> = ({ onClose }) => {
  const { t } = useTranslation('msteams', { keyPrefix: 'reviewPrompt' })
  const isMobileHost = useIsTeamsMobileHost()
  const [step, setStep] = useState(Step.rate)
  const [hovered, setHovered] = useState(0)
  const [pending, setPending] = useState<number | null>(null)
  const [feedback, setFeedback] = useState('')
  const [isSending, setIsSending] = useState(false)

  // Closing before picking a star is a dismissal; once rated, the prompt is done for good.
  // While a rating is saving it stays open: closing would record a dismissal on top of
  // the rating, and a failed save would toast with nothing left on screen.
  const handleClose = () => {
    if (pending) return
    if (step === Step.rate) {
      dismiss().catch(reportError)
    }
    onClose()
  }

  const handleRate = async (rating: number) => {
    if (pending) return
    setPending(rating)
    try {
      await submitRating(rating)
      setStep(rating === MAX_RATING ? Step.review : Step.feedback)
    } catch (err) {
      requestErrorsHandler(err)
    } finally {
      setPending(null)
    }
  }

  const handleReviewClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    trackReviewClick().catch(reportError)
    // The Teams mobile webview blocks new windows; hand the link to the host
    // (see FileDownload). Desktop and web follow the anchor.
    if (isMobileHost) {
      e.preventDefault()
      app.openLink(MARKETPLACE_REVIEW_URL).catch(reportError)
    }
    // Close on the next tick, so the anchor is still in the page when the
    // browser follows it.
    setTimeout(onClose, 0)
  }

  const handleSendFeedback = async () => {
    setIsSending(true)
    try {
      await submitFeedback(feedback.trim())
      toast.success(t('feedbackThanks'))
      onClose()
    } catch (err) {
      requestErrorsHandler(err)
    } finally {
      setIsSending(false)
    }
  }

  const highlighted = pending || hovered

  return (
    <Popup onClose={handleClose} id="review-prompt" allowCloseOnMissClick={step === Step.rate && !pending}>
      {step === Step.rate && (
        <div className="flex flex-col items-center gap-4 py-4">
          <p className="text-lg font-medium text-center px-6">{t('question')}</p>
          <div className="flex gap-2" role="group" aria-label={t('question')} onMouseLeave={() => setHovered(0)}>
            {RATINGS.map((rating) => {
              const isLit = rating <= highlighted
              return (
                <button
                  key={rating}
                  type="button"
                  aria-label={t('starLabel', { count: rating })}
                  disabled={!!pending}
                  onMouseEnter={() => setHovered(rating)}
                  onFocus={() => setHovered(rating)}
                  onBlur={() => setHovered(0)}
                  onClick={() => handleRate(rating)}
                  className="flex flex-col items-center gap-1 p-1 rounded-base hover:bg-slate-50 disabled:cursor-wait"
                >
                  {/* One icon whose fill changes, never swapped for another element, and no
                      pointer events on the children: the hover re-render lands between
                      mousedown and mouseup on a tap, and a swapped target loses the click. */}
                  <IconStar
                    size={32}
                    fill={isLit ? 'currentColor' : 'none'}
                    className={cx('pointer-events-none', isLit ? 'text-amber-400' : 'text-gray-300')}
                  />
                  <span className="pointer-events-none text-sm text-gray-500">{rating}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {step === Step.review && (
        <div className="flex flex-col items-center gap-3 py-4 text-center">
          <p className="text-lg font-medium">{t('thanksTitle')}</p>
          <p className="text-gray-600 px-2">{t('thanksText')}</p>
          <div className="flex flex-col items-center gap-1 pt-2">
            <a
              href={MARKETPLACE_REVIEW_URL}
              target="_blank"
              rel="noreferrer"
              onClick={handleReviewClick}
              className="inline-flex items-center px-4 py-2 rounded-base bg-brand_primary text-white font-medium hover:bg-violet-700 transition-colors"
            >
              {t('reviewCta')}
            </a>
            <Button type="contained" onClick={onClose}>
              {t('maybeLater')}
            </Button>
          </div>
        </div>
      )}

      {step === Step.feedback && (
        <div className="flex flex-col gap-3 pt-2">
          <p className="text-lg font-medium pr-8">{t('improveTitle')}</p>
          <Input
            type="textarea"
            name="feedback"
            value={feedback}
            onChange={(e) => setFeedback(e.currentTarget.value)}
            placeholder={t('improvePlaceholder')}
            maxLength={FEEDBACK_MAX_LENGTH}
            rows={4}
            required={false}
          />
          <div className="flex justify-end gap-3">
            <Button type="outlined" onClick={onClose} buttonType="button">
              {t('skip')}
            </Button>
            <Button onClick={handleSendFeedback} disabled={!feedback.trim()} loading={isSending}>
              {t('send')}
            </Button>
          </div>
        </div>
      )}
    </Popup>
  )
}

export default ReviewPromptPopup
